export type RecipeCategory = 'Asian' | 'Sri Lankan' | 'Mexican' | 'Latin American' | 'Desserts' | 'Drinks' | 'Sauces' | 'Other' | 'To Try';

export enum RecipeSource {
  Studio = 'studio',
  User = 'user',
  Copied = 'copied',
  Assistant = 'assistant',
  Imported = 'imported',
}

/**
 * Structured recipe layer (schema v2). Optional enrichment persisted in
 * `recipes.structured` (JSONB). The markdown string columns stay canonical —
 * this layer powers servings scaling, unit toggle and timed steps without
 * ever being the thing markdown rendering/export depends on.
 * See docs/build_plans/CHEF_STUDIO_V2_PRD.md Section 7.1.
 */
export interface StructuredIngredient {
  qty: number | null; // 450
  unit: string | null; // "g"
  imperial: string | null; // "1 lb" (display string, no runtime conversion)
  item: string; // "ground turkey"
  note: string | null; // "preferably 93% lean"
  group: string | null; // "For the Marinade"
}

export interface StructuredStep {
  text: string;
  timingMinutes: number | null;
}

export interface RecipeStructured {
  servings: { min: number; max: number } | null;
  times: { prepMinutes: number | null; cookMinutes: number | null } | null;
  equipment: string[];
  ingredients: StructuredIngredient[];
  steps: StructuredStep[];
}

export interface Recipe {
  id: string;
  title: string;
  description: string;
  category?: RecipeCategory;
  tags: string[];
  ingredients: string;
  method: string;
  notes?: string;
  image?: string; // Legacy field for backward compatibility
  image_url?: string; // New Supabase image URL
  has_custom_image?: boolean; // Flag to indicate if recipe has custom image
  dateModified: string;
  dateCreated: string;
  isFavorite: boolean; // Now user-specific
  share: boolean; // Share status (admin controls for all, users control for own)
  user_id?: string; // Recipe owner ID
  isOwner?: boolean; // Computed field: does current user own this recipe
  // User profile information for recipe creator
  creatorName?: string; // Name of the recipe creator
  creatorCountry?: string; // Country code of the recipe creator
  source?: RecipeSource; // Optional for backward compatibility
  structured?: RecipeStructured | null; // Optional schema-v2 enrichment; null/absent = legacy markdown-only
}

// RecipeFormData is the same as Recipe but without id and dates, and with optional isFavorite
// Also, explicitly omit the legacy 'image' field and ensure 'image_url' is used for form image data.
export type RecipeFormData = Omit<Recipe, 'id' | 'dateModified' | 'dateCreated' | 'isFavorite' | 'image' | 'share'> & {
  isFavorite?: boolean;
  share?: boolean; // Made optional share field
  studioPersona?: string;
  source?: RecipeSource; // Add source to form data
  // image_url is inherited from Recipe and is optional (string | undefined)
  // has_custom_image is also inherited and optional (boolean | undefined)
};
